import { Observable } from "rxjs";

import type { NullableString } from "./types";

class AttributesObserver {
    private subscriptionsCount = 0;
    private observer: MutationObserver;
    private observedElements = new WeakMap<
        HTMLElement,
        Record<string, (value: NullableString) => void>
    >();

    constructor(readonly MutationObserverClass: typeof MutationObserver) {
        this.observer = new MutationObserverClass((mutations) =>
            this.handleMutations(mutations)
        );
    }

    private handleMutations(mutations: MutationRecord[]): void {
        mutations.forEach((mutation) => {
            const element = mutation.target;
            const attributeName = mutation.attributeName;

            if (element instanceof HTMLElement && attributeName) {
                const elementAttributes = this.observedElements.get(element);

                if (elementAttributes?.[attributeName]) {
                    elementAttributes[attributeName](
                        element.getAttribute(attributeName)
                    );
                }
            }
        });
    }

    observe(
        element: HTMLElement,
        attributeName: string
    ): Observable<NullableString> {
        const attributeNameWithPrefix = `data-${attributeName}`;

        if (!this.observedElements.has(element)) {
            this.observedElements.set(element, {});
            this.observer.observe(element, {
                attributes: true,
            });

            this.subscriptionsCount++;
        }

        const elementAttributes = this.observedElements.get(element);

        return new Observable<NullableString>((subscriber) => {
            if (!elementAttributes) {
                subscriber.error(new Error("Element not observed"));
                return;
            }

            elementAttributes[attributeNameWithPrefix] = (
                value: NullableString
            ) => subscriber.next(value);

            const currentValue = element.getAttribute(attributeNameWithPrefix);

            subscriber.next(currentValue);

            return () => {
                delete elementAttributes[attributeNameWithPrefix];

                if (Object.keys(elementAttributes).length === 0) {
                    this.observedElements.delete(element);
                    this.subscriptionsCount--;
                }

                if (this.subscriptionsCount === 0) {
                    this.observer.disconnect();
                }
            };
        });
    }
}

export default AttributesObserver;
